// The Overview's cleaning tile (HACCP module 3, docs/architecture.md §22.6), in each site's header: "Nettoyage : X faites
// / Y dues aujourd'hui", the overdue ones in the alarm tone, linking to the site's cleaning checklist. Shown only when
// the plan has the cleaning plan and the site has occurrences today.

import type { CleaningSiteDay } from '@cck/schema';
import { Link } from 'react-router';
import { useI18n } from '../i18n/index.ts';
import { useCleaningToday, useHaccpSettings } from '../lib/queries.ts';
import { cx } from './ui.tsx';

/** Today's cleaning of every site, by site ID (empty without the feature). */
export function useCleaningBySite(orgId: string): Map<string, CleaningSiteDay> {
  const settings = useHaccpSettings(orgId);
  const today = useCleaningToday(orgId, settings.data?.features.cleaning === true);
  return new Map((settings.data?.features.cleaning ? today.data?.data ?? [] : []).map((d) => [d.site_id, d]));
}

export function CleaningTile({ day }: { day: CleaningSiteDay | undefined }) {
  const { t } = useI18n();
  if (!day || day.occurrences.length === 0) return null;
  return (
    <Link to={`haccp/cleaning?site=${day.site_id}`}
      className={cx('inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium normal-case tracking-normal',
        day.overdue > 0 ? 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100'
          : day.due === 0 ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50')}>
      <span aria-hidden>🧽</span>
      <span>{t('cleaning.tile', { done: day.done, total: day.done + day.due + day.overdue })}</span>
      {day.overdue > 0 && <span className="font-semibold">· {t('cleaning.tileOverdue', { count: day.overdue })}</span>}
    </Link>
  );
}
